import "client-only"

import { AuthType } from "@/lib/auth-type"
import { getConfigParams } from "@/lib/get-config-params"
import useGlobalParamsStore from "@/store/global-params-store"
import { Result } from "@pacote/result"
import { v4 as uuidV4 } from "uuid"
import {
  createFetcher,
  FetcherArgs,
  FetcherErrorResponse,
} from "./create-fetcher"

type DefaultData = {
  channel: number
  ipAddress: string
  requestId: string
  infoChannel?: string // Make this property optional
}

type DefaultHeaders = {
  "X-Call-Id": string
  "X-Session-Id": string
  "X-Channel-Id": string
  "X-Channel-Info": string
}

type ClientFetcherConfig = {
  enrichRequestData?: boolean
}

type AuthClientFetcherConfig = ClientFetcherConfig & {
  allowSsoUrlRewrite?: boolean
}

const addDefaults = (data: any = {}) => {
  const { channel } = getConfigParams()
  const { ipAddress } = useGlobalParamsStore.getState()

  const defaultData: DefaultData = {
    channel,
    ipAddress,
    requestId: uuidV4(),
  }

  if ("userAgent" in data) delete defaultData.infoChannel

  return {
    ...defaultData,
    ...data,
  }
}

const getDefaultHeaders = (): DefaultHeaders => {
  const { channel } = getConfigParams()

  return {
    "X-Call-Id": uuidV4(),
    "X-Session-Id": sessionStorage.getItem("sessionId") ?? uuidV4(),
    "X-Channel-Id": channel.toString(),
    "X-Channel-Info": navigator.userAgent,
  }
}

const isVerbAllowingRequestBody = (verb?: string) => {
  return verb != null && ["POST", "PUT", "PATCH"].includes(verb)
}

/**
 * Builds the SSO endpoint for the given endpoint. This is done because oauth and
 * sso flows are based on a different jwt token, and the services are exposed in
 * a separate endpoint. The SSO endpoint must follow the pattern: /{microservice}/{version}/sso/{rest},
 * if they don't, they have to be modified in the back-end accordingly.
 * Do not modify this logic.
 *
 * @param endpoint
 * @returns sso endpoint
 */
const buildSsoEndpoint = (endpoint: string) => {
  if (endpoint.includes("/sso/")) return endpoint

  const urlParts = endpoint.split("/")

  // exclude the original version and the word "ms" from the url
  const version = urlParts.find((urlPart) => /^v\d+$/.test(urlPart)) ?? "v1"

  const strippedUrlParts = urlParts.filter((urlPart) => {
    return urlPart !== "" && urlPart !== "ms" && !/^v\d+$/.test(urlPart)
  })

  const [microservice, ...rest] = strippedUrlParts
  const ssoEndpoint = `/${microservice}/${version}/sso/${rest.join("/")}`
  return ssoEndpoint
}

export const baseClientFetcher = <S, E>(
  arg: string | FetcherArgs,
  config?: ClientFetcherConfig,
): Promise<Result<S, FetcherErrorResponse<E>>> => {
  const baseUrl = window.APP_API_URL

  if (!baseUrl) {
    throw new Error("window.APP_API_URL is not defined")
  }

  const fetchBaseQuery = createFetcher({ baseUrl })

  // The new "core" services have common data in the headers, while the legacy
  // ones have it in the request body / params. Until the migration is complete,
  // this behavior defaults to true.
  const enrichRequestData = config?.enrichRequestData ?? true

  if (typeof arg === "string") {
    // arg is directly the url

    return fetchBaseQuery({
      url: arg,
      headers: getDefaultHeaders(),
      ...(enrichRequestData ? { params: addDefaults() } : {}),
    })
  } else {
    // arg is FetcherArgs

    const argsWithDefaultHeaders: FetcherArgs = {
      ...arg,
      headers: {
        ...getDefaultHeaders(),
        ...arg.headers,
      },
    }

    if (enrichRequestData) {
      return fetchBaseQuery({
        ...argsWithDefaultHeaders,
        ...(isVerbAllowingRequestBody(arg.method)
          ? { body: addDefaults(arg.body) }
          : { params: addDefaults(arg.params) }),
      })
    } else {
      return fetchBaseQuery(argsWithDefaultHeaders)
    }
  }
}

export const withAuthClientFetcher = <S, E>(
  arg: string | FetcherArgs,
  config?: AuthClientFetcherConfig,
): Promise<Result<S, FetcherErrorResponse<E>>> => {
  const { authType } = getConfigParams()

  const authHeaders = {
    Authorization: `Bearer ${useGlobalParamsStore.getState().token}`,
  }

  const getUrl = (origUrl: string) => {
    const allowSsoUrlRewrite = config?.allowSsoUrlRewrite ?? true

    return allowSsoUrlRewrite &&
      (authType === AuthType.Sso || authType === AuthType.OAuth)
      ? buildSsoEndpoint(origUrl)
      : origUrl
  }

  if (typeof arg === "string") {
    return baseClientFetcher({ url: getUrl(arg), headers: authHeaders })
  } else {
    return baseClientFetcher({
      ...arg,
      url: getUrl(arg.url),
      headers: {
        ...arg.headers,
        ...authHeaders,
      },
    })
  }
}

export const baseExternalFetcher = createFetcher({})
